import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, rawFetch } from "@/lib/api"
import { useEffect, useRef } from "react"
import { auth } from "@/lib/auth"
import { Badge, Button, Card, CardBody, CardHeader, Input, Spinner, Stub, Textarea, Toast } from "@/components/ui"
import { timeAgo } from "@/lib/utils"
import type { MemoryOut, PersonaConfig, PersonalityOut, ProfileFactOut } from "@/lib/types"
import { ImageIcon, SaveIcon, SearchIcon, TrashIcon, UploadIcon, XIcon } from "lucide-react"

export const Route = createFileRoute("/_app/memory")({
  component: MemoryPage,
})

function MemoryPage() {
  const me = auth.me()
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  const [includeExpired, setIncludeExpired] = useState(false)

  const memories = useQuery({
    queryKey: ["memories", me?.id, q, includeExpired],
    queryFn: () => q
      ? api<MemoryOut[]>(`/api/dashboard/users/${me?.id}/memory/search`, {
          method: "POST",
          body: { query: q, limit: 50, include_expired: includeExpired },
        })
      : api<MemoryOut[]>(`/api/dashboard/users/${me?.id}/memories`, {
          query: { limit: 100, include_expired: includeExpired },
        }),
    enabled: !!me?.id,
  })

  // Profile facts come bundled inside UserDetailOut, not a standalone
  // endpoint. Cheaper than a second round-trip and matches what the Mac
  // client does.
  const facts = useQuery({
    queryKey: ["profile-facts", me?.id],
    queryFn: async () => {
      const detail = await api<{ profile_facts: ProfileFactOut[] }>(
        `/api/dashboard/users/${me?.id}`,
      )
      return detail.profile_facts || []
    },
    enabled: !!me?.id,
  })

  const personality = useQuery({
    queryKey: ["personality"],
    queryFn: () => api<PersonalityOut>("/api/dashboard/settings/personality"),
  })

  const forget = useMutation({
    mutationFn: (id: string) => api(`/api/dashboard/memories/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memories"] }),
  })

  const forgetFact = useMutation({
    mutationFn: (key: string) => api(`/api/dashboard/users/${me?.id}/profile_facts/${encodeURIComponent(key)}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profile-facts"] }),
  })

  // Sync the editor once the GET resolves. Only seed the textarea on
  // first load (pp === "") so the user's in-flight edits aren't blown
  // away by a refetch.
  const [pp, setPp] = useState<string>("")
  if (personality.data && pp === "" && personality.data.content) {
    queueMicrotask(() => setPp(personality.data!.content))
  }
  const persona = parsePersona(pp)
  function updatePersona<K extends keyof PersonaConfig>(key: K, value: PersonaConfig[K]) {
    const next = { ...persona, [key]: value }
    setPp(JSON.stringify(next, null, 2))
  }

  const savePersonality = useMutation({
    mutationFn: (content: string) => api("/api/dashboard/settings/personality", {
      method: "PUT",
      body: { content },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["personality"] }),
  })

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Memory</h1>
        <p className="text-[13px] text-muted-foreground mt-1.5">
          What Lucy knows about you. Tune presence, review facts, forget anything.
        </p>
      </div>

      <Card>
        <CardHeader title="Personality" subtitle="Tune Lucy's presence without hand-editing a system prompt." accent="hsl(252 96% 67%)" />
        <CardBody className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <SelectField
              label="Tone"
              value={persona.tone}
              onChange={(v) => updatePersona("tone", v as PersonaConfig["tone"])}
              options={[
                ["warm_witty", "Warm + witty"],
                ["calm", "Calm"],
                ["executive", "Executive"],
                ["playful", "Playful"],
              ]}
            />
            <SelectField
              label="Directness"
              value={persona.directness}
              onChange={(v) => updatePersona("directness", v as PersonaConfig["directness"])}
              options={[
                ["direct", "Direct"],
                ["gentle", "Gentle"],
                ["sharp", "Sharp"],
              ]}
            />
            <SelectField
              label="Proactivity"
              value={persona.proactivity}
              onChange={(v) => updatePersona("proactivity", v as PersonaConfig["proactivity"])}
              options={[
                ["balanced", "Balanced"],
                ["low", "Low"],
                ["high", "High"],
              ]}
            />
            <SelectField
              label="Teasing"
              value={persona.teasing}
              onChange={(v) => updatePersona("teasing", v as PersonaConfig["teasing"])}
              options={[
                ["light", "Light"],
                ["never", "Never"],
                ["familiar", "Familiar"],
              ]}
            />
            <SelectField
              label="Language"
              value={persona.language}
              onChange={(v) => updatePersona("language", v as PersonaConfig["language"])}
              options={[
                ["mirror_user", "Mirror user"],
                ["greek", "Greek"],
                ["english", "English"],
              ]}
            />
            <SelectField
              label="Presence"
              value={persona.presence}
              onChange={(v) => updatePersona("presence", v as PersonaConfig["presence"])}
              options={[
                ["companion", "Companion — in character, has a place and a day"],
                ["assistant", "Assistant — says it's software when asked"],
              ]}
            />
            <SelectField
              label="Boundaries"
              value={persona.boundary_mode}
              onChange={(v) => updatePersona("boundary_mode", v as PersonaConfig["boundary_mode"])}
              options={[
                ["professional", "Professional"],
                ["strict", "Strict"],
              ]}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <label className="text-xs font-medium text-muted-foreground">Humor</label>
              <Badge variant="default">{persona.humor_level}/3</Badge>
            </div>
            <input
              type="range"
              min={0}
              max={3}
              step={1}
              value={persona.humor_level}
              onChange={(e) => updatePersona("humor_level", Number(e.target.value) as PersonaConfig["humor_level"])}
              className="lucy-range"
              style={{
                background: `linear-gradient(90deg, #F3B757 0%, #F3B757 ${(persona.humor_level / 3) * 100}%, rgba(255,255,255,0.08) ${(persona.humor_level / 3) * 100}%, rgba(255,255,255,0.08) 100%)`,
              }}
            />
            <div className="flex justify-between text-[11px] text-[#6b6f88]">
              <span>Dry</span>
              <span>Full comedian</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium">Appearance</label>
            <Textarea
              rows={3}
              value={persona.appearance}
              placeholder="How Lucy looks in pictures of herself: age, hair, eyes, build, style… Used together with the reference photo below."
              onChange={(e) => updatePersona("appearance", e.target.value)}
            />
          </div>

          <ReferencePhoto hasImage={!!personality.data?.has_reference_image} />

          <div className="space-y-1.5">
            <label className="text-xs font-medium">Custom notes</label>
            <Textarea
              rows={5}
              value={persona.custom_notes}
              placeholder="Specific instructions: when to be playful, topics to treat carefully, preferred language quirks…"
              onChange={(e) => updatePersona("custom_notes", e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={() => savePersonality.mutate(pp)} disabled={savePersonality.isPending}>
              {savePersonality.isPending ? <Spinner /> : <SaveIcon className="h-3 w-3" />} Save
            </Button>
            {savePersonality.isSuccess && <Toast variant="ok">Saved.</Toast>}
          </div>
        </CardBody>
      </Card>

      <ReviewCard userId={me?.id} />

      <Card>
        <CardHeader title="Profile facts" subtitle="Inferred facts about you. Keys, not narratives." accent="hsl(158 64% 52%)" />
        <CardBody className="space-y-2">
          {facts.isLoading && <Spinner />}
          {facts.data?.length === 0 && <Stub title="No profile facts yet" />}
          <div className="flex flex-col gap-2">
            {facts.data?.map((f) => (
              <div key={f.key} className="flex items-center gap-3 rounded-xl border border-border bg-foreground/[0.04] px-3.5 py-3 transition hover:bg-foreground/[0.06]">
                <span className="font-mono text-[12.5px] text-mint">{f.key}</span>
                <span className="text-[#4a4d63]">=</span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-[#cfd2e2]">{f.value}</span>
                <Badge variant="muted">{(f.confidence * 100).toFixed(0)}%</Badge>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                  if (window.confirm(`Forget ${f.key}?`)) forgetFact.mutate(f.key)
                }}>
                  <XIcon className="h-3.5 w-3.5 hover:text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Memories"
          accent="hsl(255 92% 76%)"
          subtitle={`${memories.data?.length ?? 0} stored. Searchable.`}
          right={
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={includeExpired}
                onChange={(e) => setIncludeExpired(e.target.checked)}
              />
              Expired
            </label>
          }
        />
        <CardBody className="space-y-3">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search memories"
              className="pl-9"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {memories.isLoading && <Spinner />}
          {memories.data?.length === 0 && <Stub title="No memories" hint="They appear as Lucy learns about you." />}
          <div className="flex flex-col gap-2">
            {memories.data?.map((m) => (
              <div key={m.id} className="flex items-start justify-between gap-3 rounded-xl border border-border bg-foreground/[0.04] p-3.5 transition hover:bg-foreground/[0.06]">
                <div className="min-w-0 text-sm">
                  <p className="text-foreground">{m.text}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[10.5px] text-muted-foreground">
                    <Badge variant="default">{m.kind}</Badge>
                    {isExpired(m) && <Badge variant="warn">expired</Badge>}
                    <span>salience {m.salience.toFixed(2)} · {timeAgo(m.created_at)}</span>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => {
                  if (window.confirm("Forget this memory?")) forget.mutate(m.id)
                }}>
                  <TrashIcon className="h-3.5 w-3.5 hover:text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

function isExpired(memory: MemoryOut): boolean {
  return !!memory.expires_at && new Date(memory.expires_at).getTime() <= Date.now()
}

const DEFAULT_PERSONA: PersonaConfig = {
  version: 1,
  preset: "executive_companion",
  tone: "warm_witty",
  humor_level: 2,
  directness: "direct",
  proactivity: "balanced",
  teasing: "light",
  language: "mirror_user",
  boundary_mode: "professional",
  presence: "companion",
  custom_notes: "",
  appearance: "",
}

function parsePersona(content: string): PersonaConfig {
  if (!content.trim()) return DEFAULT_PERSONA
  try {
    const raw = JSON.parse(content) as Partial<PersonaConfig>
    if (raw.version !== 1) throw new Error("legacy")
    return {
      ...DEFAULT_PERSONA,
      ...raw,
      humor_level: clampHumor(raw.humor_level),
      custom_notes: String(raw.custom_notes || ""),
    }
  } catch {
    return { ...DEFAULT_PERSONA, custom_notes: content }
  }
}

function clampHumor(value: unknown): PersonaConfig["humor_level"] {
  const n = Math.max(0, Math.min(3, Number(value || 0)))
  return n as PersonaConfig["humor_level"]
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: [string, string][]
  onChange: (value: string) => void
}) {
  return (
    <label className="space-y-2 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-border bg-black/25 px-3.5 py-2.5 text-sm font-normal text-foreground focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary/40 [&>option]:bg-card"
      >
        {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
      </select>
    </label>
  )
}


/** Lucy's reference photo for self-portraits (admin-only upload). */
function ReferencePhoto({ hasImage }: { hasImage: boolean }) {
  const qc = useQueryClient()
  const me = auth.me()
  const input = useRef<HTMLInputElement | null>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const isAdmin = me?.tier === "admin"

  useEffect(() => {
    let url: string | null = null
    if (!hasImage) { setSrc(null); return }
    rawFetch("/api/dashboard/settings/personality/reference").then(async (r) => {
      if (!r.ok) return
      url = URL.createObjectURL(await r.blob())
      setSrc(url)
    }).catch(() => undefined)
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [hasImage])

  async function upload(file: File) {
    setBusy(true); setErr(null)
    try {
      const fd = new FormData()
      fd.append("file", file, file.name)
      await api("/api/dashboard/settings/personality/reference", { method: "POST", rawBody: fd })
      qc.invalidateQueries({ queryKey: ["personality"] })
    } catch (e) {
      setErr((e as { detail?: string }).detail || (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true); setErr(null)
    try {
      await api("/api/dashboard/settings/personality/reference", { method: "DELETE" })
      qc.invalidateQueries({ queryKey: ["personality"] })
    } catch (e) {
      setErr((e as { detail?: string }).detail || (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium">Reference photo</label>
      <div className="flex items-center gap-4 rounded-xl border border-border bg-foreground/[0.04] p-3">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-black/30">
          {src ? <img src={src} alt="Lucy reference" className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="text-xs text-muted-foreground">
            When you ask for a picture of Lucy, image-to-image providers (OpenAI, Gemini, Venice) use this photo so her face stays the same. Others fall back to the appearance text.
          </p>
          <div className="flex items-center gap-2">
            <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = "" }} />
            <Button size="sm" variant="outline" disabled={busy || !isAdmin} onClick={() => input.current?.click()}>
              {busy ? <Spinner /> : <UploadIcon className="h-3 w-3" />} {hasImage ? "Replace" : "Upload"}
            </Button>
            {hasImage && (
              <Button size="sm" variant="ghost" disabled={busy || !isAdmin} onClick={remove}>
                <XIcon className="h-3 w-3" /> Remove
              </Button>
            )}
            {!isAdmin && <span className="text-[11px] text-muted-foreground">Admin only.</span>}
          </div>
          {err && <Toast variant="destructive">{err}</Toast>}
        </div>
      </div>
    </div>
  )
}


const REVIEW_SEEN_KEY = "lucy.web.memory.reviewed"

function readSeen(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(REVIEW_SEEN_KEY) || "{}") || {} } catch { return {} }
}

/** "Here's what I learned recently — keep, correct or forget." */
function ReviewCard({ userId }: { userId?: string }) {
  const qc = useQueryClient()
  const [seen, setSeen] = useState<Record<string, string>>(readSeen)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState("")
  const recent = useQuery({
    queryKey: ["memory-recent", userId],
    queryFn: () => api<{ memories: MemoryOut[]; facts: { key: string; value: string; confidence: number; updated_at: string | null }[] }>(
      `/api/dashboard/users/${userId}/memories/recent`, { query: { hours: 168 } },
    ),
    enabled: !!userId,
  })
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["memory-recent"] })
    qc.invalidateQueries({ queryKey: ["memories"] })
    qc.invalidateQueries({ queryKey: ["profile-facts"] })
    qc.invalidateQueries({ queryKey: ["my-notifications"] })
  }
  function markSeen(id: string) {
    const next = { ...seen, [id]: new Date().toISOString() }
    setSeen(next)
    try { localStorage.setItem(REVIEW_SEEN_KEY, JSON.stringify(next)) } catch { /* ignore */ }
  }
  const keep = useMutation({
    mutationFn: (id: string) => api(`/api/dashboard/memories/${id}`, { method: "PATCH", body: { keep: true } }),
    onSuccess: (_d, id) => { markSeen(id); invalidate() },
  })
  const correct = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => api(`/api/dashboard/memories/${id}`, { method: "PATCH", body: { text } }),
    onSuccess: (_d, v) => { markSeen(v.id); setEditing(null); invalidate() },
  })
  const forget = useMutation({
    mutationFn: (id: string) => api(`/api/dashboard/memories/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  })
  const fixFact = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api(`/api/dashboard/users/${userId}/profile_facts/${encodeURIComponent(key)}`, { method: "PATCH", body: { value } }),
    onSuccess: (_d, v) => { markSeen(`fact:${v.key}`); setEditing(null); invalidate() },
  })
  const forgetFact = useMutation({
    mutationFn: (key: string) => api(`/api/dashboard/users/${userId}/profile_facts/${encodeURIComponent(key)}`, { method: "DELETE" }),
    onSuccess: invalidate,
  })

  const mems = (recent.data?.memories ?? []).filter((m) => !seen[m.id])
  const facts = (recent.data?.facts ?? []).filter((f) => !seen[`fact:${f.key}`] || (f.updated_at && seen[`fact:${f.key}`] < f.updated_at))
  const total = mems.length + facts.length

  return (
    <Card>
      <CardHeader
        title="What I learned recently"
        subtitle={total ? `${total} new since you last looked. Confirm, correct or forget — this is how Lucy gets you right.` : "Nothing new in the last 7 days."}
        accent="hsl(38 92% 60%)"
      />
      {total > 0 && (
        <CardBody className="space-y-2">
          {recent.isLoading && <Spinner />}
          {facts.map((f) => (
            <div key={`f-${f.key}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-foreground/[0.04] px-3.5 py-2.5 text-sm">
              <span className="font-mono text-[12px] text-mint">{f.key}</span>
              {editing === `fact:${f.key}` ? (
                <form className="flex min-w-0 flex-1 items-center gap-2" onSubmit={(e) => { e.preventDefault(); fixFact.mutate({ key: f.key, value: draft }) }}>
                  <Input value={draft} onChange={(e) => setDraft(e.target.value)} className="h-8" autoFocus />
                  <Button size="sm" type="submit" disabled={fixFact.isPending}>Save</Button>
                  <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(null)}>Cancel</Button>
                </form>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate text-[#cfd2e2]">{f.value}</span>
                  <Button size="sm" variant="outline" onClick={() => markSeen(`fact:${f.key}`)}>Correct</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDraft(f.value); setEditing(`fact:${f.key}`) }}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => forgetFact.mutate(f.key)}>Forget</Button>
                </>
              )}
            </div>
          ))}
          {mems.map((m) => (
            <div key={m.id} className="rounded-xl border border-border bg-foreground/[0.04] px-3.5 py-2.5 text-sm">
              {editing === m.id ? (
                <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); correct.mutate({ id: m.id, text: draft }) }}>
                  <Textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
                  <div className="flex gap-2">
                    <Button size="sm" type="submit" disabled={correct.isPending || !draft.trim()}>Save correction</Button>
                    <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                </form>
              ) : (
                <div className="flex flex-wrap items-start gap-2">
                  <p className="min-w-0 flex-1 text-foreground">{m.text}</p>
                  <div className="flex shrink-0 items-center gap-1">
                    <Badge variant="default">{m.kind}</Badge>
                    <Button size="sm" variant="outline" onClick={() => keep.mutate(m.id)}>Correct</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setDraft(m.text); setEditing(m.id) }}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => forget.mutate(m.id)}>Forget</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardBody>
      )}
    </Card>
  )
}
